import React, { lazy, Suspense, useRef, useEffect, useState } from 'react'
import { MemoryRouter, useHistory, useLocation } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import { EscHandlerProvider } from '@fs/zion-ui/dist/es/use-esc-handler'
import ConnectButton from './ConnectButton'
import { useChatState } from '../../state/ChatState'
import GlobalChatListener from './GlobalChatListener'
import useChatRoomInvite from '../../hooks/useChatRoomInvite'

const LoggedOutDialog = lazy(() => import('./LoggedOutDialog'))
const ChatOverlay = lazy(() => import('./ChatOverlay'))

function ChatStructure({ loading, isSupportPage }) {
  const user = useUser()
  const { connection, failedToConnect, chatEnabled } = useChatState()
  const connectButtonRef = useRef()
  const history = useHistory()

  // we have a use case where UX wants us to hide the overlay and button when
  // the user is interacting on the page on small screens (like typing in a search bar)
  // this exposes an means of allowing for that behavior globally.
  const [globallyHidden, setGloballyHidden] = useState(false)
  useEffect(() => {
    window.showChat = () => setGloballyHidden(false)
    window.hideChat = () => setGloballyHidden(true)
  }, [setGloballyHidden])

  // Since the upper app needs access to the chat history to be able to set its destination,
  // this was the easiest (and possibly most appropriate) way to make it available.
  window.chatHistory = history

  if (!chatEnabled) return null

  // Hide the connect button while the user is loading
  // Also hide on the support page, since there's a different chat window there
  if (loading || isSupportPage || globallyHidden) return null

  // NOTE: If the user is not logged in, always show the LoggedOutDialog.
  // Otherwise, if we have a connection, we've established a connection to the chat server.
  // In the case where we haven't connected or chat is not enabled, we have custom screens to show the user.
  return user?.signedIn && (connection || failedToConnect || !chatEnabled) ? (
    <>
      <ConnectButton ref={connectButtonRef} />
      <ChatOverlay />
    </>
  ) : (
    <EscHandlerProvider>
      <ConnectButton ref={connectButtonRef} />
      <LoggedOutDialog connectButtonRef={connectButtonRef} />
    </EscHandlerProvider>
  )
}

export default function Chat() {
  const user = useUser()
  const [joinChatRoom] = useChatRoomInvite()
  const { connection } = useChatState()
  const location = useLocation()
  const chatRoom = location.state?.chatRoom

  useEffect(() => {
    // Chat links will navigate the user to the home page with route state for the chat room to join
    // If a chatRoom is in the route state, then attempt to join it
    if (chatRoom && connection) {
      joinChatRoom(chatRoom)
    }
  }, [connection, joinChatRoom, chatRoom])

  const handleLoseDraft = (message, callback) => {
    // eslint-disable-next-line no-alert
    const result = window.confirm(message)
    callback(result)
  }

  return (
    <Suspense fallback={<span />}>
      {/* MemoryRouter creates its own internal history object, so we cannot pass it a custom one. :( */}
      <MemoryRouter getUserConfirmation={handleLoseDraft}>
        {/* I first tried just rendering null on the support page, but apparently something else being brought along in this component is required for making everything work with the support page chat. So rather than trying to figure out what piece(s) to duplicate there, this does the same thing but easier */}
        <ChatStructure loading={user.userLoading} isSupportPage={location.pathname === '/volunteer/support'} />
        <GlobalChatListener fsUser={user} />
      </MemoryRouter>
    </Suspense>
  )
}
